import {
  addressValidationSchema,
  birthDateValidationSchema,
  cityValidationSchema,
  emailValidationSchema,
  firstNameValidationSchema,
  lastNameValidationSchema,
  phoneNumberValidationSchema,
  postalCodeValidationSchema,
} from '@/schemas/ValidationSchema.ts';
import { z } from 'zod';
import CustomFieldDefinitionDto from '@/features/dashboard/tourCustomFields/types/_CustomFieldDefinitionDto.ts';
import {
  participantTourOptionsSchema,
  SelectedOptionEntry,
} from '@/features/public/booking/schemas/participantTourOptionsSchema.ts';
import { buildCustomFieldsSchema } from '@/features/public/booking/schemas/participantCustomFieldsSchema.ts';
import { BookingTourBasicDataDto } from '@/features/public/booking/types/BookingTourBasicDataDto.ts';
import { BookingInsurancesDto } from '@/features/public/booking/types/BookingInsurancesDto.ts';
import { FillerType } from '@/features/public/booking/types/FillerType.ts';
import { calculateAge } from '@/utils/ageUtils.ts';
import { pluralForm } from '@/utils/pluralize.ts';
import {
  formatAgeRangesErrorMessage,
  isAgeWithinAnyRange,
  requiresAgeValidation,
} from '@/features/public/booking/schemas/ageRestrictionValidation.ts';

export const participantSchema = z.object({
  firstName: firstNameValidationSchema,
  lastName: lastNameValidationSchema,
  birthDate: birthDateValidationSchema,
  selectedInsuranceId: z.string().optional(),
  transportVariantId: z.string().optional(),
});

export const clientSchema = z.object({
  fillerType: z.enum([FillerType.PARTICIPANT, FillerType.CLIENT]),
  firstName: firstNameValidationSchema,
  lastName: lastNameValidationSchema,
  birthDate: birthDateValidationSchema,
  email: emailValidationSchema,
  phoneNumber: phoneNumberValidationSchema,
  address: addressValidationSchema,
  postalCode: postalCodeValidationSchema,
  city: cityValidationSchema,
  participants: z
    .array(participantSchema)
    .min(1, 'Wymagany jest co najmniej jeden uczestnik'),
});

export type ClientInputForm = z.input<typeof clientSchema>;
export type ClientForm = z.infer<typeof clientSchema>;

// Extended types to support dynamic custom fields and booking options
export type ParticipantInputFormWithCustomFields = z.input<
  typeof participantSchema
> & {
  customFields?: Record<string, string | boolean | number | Date>;
  selectedOptions?: Record<string, SelectedOptionEntry>;
};

export type ParticipantFormWithCustomFields = z.infer<
  typeof participantSchema
> & {
  customFields?: Record<string, string | boolean | number | Date>;
  selectedOptions?: Record<string, SelectedOptionEntry>;
};

export type ClientInputFormWithCustomFields = Omit<
  ClientInputForm,
  'participants'
> & {
  participants: ParticipantInputFormWithCustomFields[];
};

export type ClientFormWithCustomFields = Omit<ClientForm, 'participants'> & {
  participants: ParticipantFormWithCustomFields[];
};

export const generateFormKey = (
  clientForm: ClientForm | ClientFormWithCustomFields,
) => {
  const optionsKey = (clientForm as ClientFormWithCustomFields).participants
    ?.map((p) =>
      Object.entries(p.selectedOptions ?? {})
        .map(
          ([optionId, entry]) =>
            `${optionId}:${entry?.selections?.map((s) => `${s.variantId}-${s.priceId}`).join('|') ?? ''}`,
        )
        .join(','),
    )
    .join(';');

  const transportKey = clientForm.participants
    ?.map((p) => p.transportVariantId ?? '')
    .join(',');

  return `fillerType: ${clientForm.fillerType},
  participants: ${clientForm.participants?.length},
  ages: ${clientForm.participants?.map((p: { birthDate: Date }) => p.birthDate).join(',')},
  options: ${optionsKey},
  transport: ${transportKey}`;
};

export const createParticipantSchema = (
  customFields: CustomFieldDefinitionDto[],
) => {
  if (customFields.length === 0) {
    return participantSchema.extend({
      selectedOptions: participantTourOptionsSchema,
    });
  }

  return participantSchema.extend({
    customFields: buildCustomFieldsSchema(customFields),
    selectedOptions: participantTourOptionsSchema,
  });
};

export const createClientSchema = (
  customFields: CustomFieldDefinitionDto[],
  tourBasicInfo?: BookingTourBasicDataDto,
  bookingInsurances?: BookingInsurancesDto | null,
) => {
  const extendedParticipantSchema = createParticipantSchema(customFields);

  return clientSchema
    .extend({
      participants: z
        .array(extendedParticipantSchema)
        .min(1, 'Wymagany jest co najmniej jeden uczestnik'),
    })
    .superRefine((data, ctx) => {
      if (bookingInsurances?.required) {
        data.participants.forEach((participant, index) => {
          if (!participant.selectedInsuranceId) {
            ctx.addIssue({
              code: 'custom',
              message: 'Wybór ubezpieczenia jest wymagany',
              path: ['participants', index, 'selectedInsuranceId'],
            });
          }
        });
      }

      if (tourBasicInfo?.transport?.mandatory) {
        data.participants.forEach((participant, index) => {
          if (!participant.transportVariantId) {
            ctx.addIssue({
              code: 'custom',
              message: 'Wybór transportu jest wymagany',
              path: ['participants', index, 'transportVariantId'],
            });
          }
        });
      }

      const { prices, startDate, hasLimit, availableParticipantSpots } =
        tourBasicInfo ?? {};

      if (
        hasLimit &&
        availableParticipantSpots != null &&
        data.participants.length > availableParticipantSpots
      ) {
        const requestedCount = data.participants.length;
        const spotsWord = pluralForm(
          availableParticipantSpots,
          'wolne miejsce',
          'wolne miejsca',
          'wolnych miejsc',
        );
        ctx.addIssue({
          code: 'custom',
          message: `Niestety nie możemy zarezerwować ${requestedCount} miejsc, ponieważ na ten wyjazd zostało już tylko ${availableParticipantSpots} ${spotsWord}`,
          path: ['participants'],
        });
      }

      if (!prices || !startDate || !requiresAgeValidation(prices)) return;

      const referenceDate = new Date(startDate);
      const errorMessage = formatAgeRangesErrorMessage(prices);

      if (data.fillerType === FillerType.PARTICIPANT && data.birthDate) {
        const age = calculateAge(data.birthDate, referenceDate);
        if (!isAgeWithinAnyRange(age, prices)) {
          ctx.addIssue({
            code: 'custom',
            message: errorMessage,
            path: ['birthDate'],
          });
        }
      }

      data.participants.forEach((participant, index) => {
        if (data.fillerType === FillerType.PARTICIPANT && index === 0) return;

        if (participant.birthDate) {
          const age = calculateAge(participant.birthDate, referenceDate);
          if (!isAgeWithinAnyRange(age, prices)) {
            ctx.addIssue({
              code: 'custom',
              message: errorMessage,
              path: ['participants', index, 'birthDate'],
            });
          }
        }
      });
    });
};
